import { useContext } from 'react';
import { ProfileCard } from '@/components/common/ProfileCard';
import { ProfileHideEmptyContext } from '@/components/common/ProfileFacts';
import { ProfileChip } from '@/components/common/ProfileChip';
import { ProfileLogo, type ProfileLogoProps } from '@/components/common/ProfileLogo';
import { ProfileMediaRow } from '@/components/common/ProfileMediaRow';
import { Button } from '@/components/ui/Button';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import { formatBytes } from '@/lib/utils/format-bytes';
import type { StartupFoundingTeamMember } from '../../api/startup-profile.api';
import { STARTUP_PROFILE_STRINGS as T } from './startup-profile.strings';
import type { StartupCardProps } from './StartupDetailCards';

// Initials tiles cycle through the mockup's three brand colours.
const MEMBER_TONES: NonNullable<ProfileLogoProps['tone']>[] = ['teal2', 'teal', 'maroon'];

const LINK_CLASS = 'text-sm font-medium text-dkgreen hover:text-dkgreen-hover';

function MemberTile({ member, index }: { member: StartupFoundingTeamMember; index: number }) {
  return (
    <li className="bg-page-bg flex flex-col items-center rounded-2xl p-5 text-center">
      <ProfileLogo
        name={member.name}
        size={56}
        shape="circle"
        tone={MEMBER_TONES[index % MEMBER_TONES.length]}
      />
      <p className="text-navy mt-2.5 font-semibold">{member.name}</p>
      <p className="text-subtext text-[0.8125rem]">{member.title}</p>
      {member.pastExits && (
        <span className="mt-2.5">
          <ProfileChip tone="green">{T.PAST_EXIT}</ProfileChip>
        </span>
      )}
      {member.linkedinUrl && (
        <a
          href={member.linkedinUrl}
          target="_blank"
          rel="noreferrer"
          className="text-dkgreen hover:text-dkgreen-hover mt-2.5 text-[0.8125rem]"
        >
          {T.LINKEDIN}
        </a>
      )}
    </li>
  );
}

/** Founding team (mockup: 7 of 12 columns, one tile per member, three to a row). */
export function FoundingTeamCard({ data, action }: StartupCardProps) {
  const team = data.foundingTeam ?? [];
  return (
    <ProfileCard title={T.FOUNDING_TEAM} action={action}>
      {team.length === 0 ? (
        <p className="text-input text-subtext">{T.NO_TEAM}</p>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {team.map((member, index) => (
            <MemberTile key={`${member.name}-${index}`} member={member} index={index} />
          ))}
        </ul>
      )}
    </ProfileCard>
  );
}

function fileNameOf(key: string): string {
  return key.split('/').pop() || T.PITCH_DECK;
}

function DeckRow({ data, onAdd }: StartupCardProps) {
  if (!data.pitchDeckUrl) {
    const add = onAdd ? (
      <Button variant="link-sm" onClick={onAdd}>
        {L.ADD}
      </Button>
    ) : undefined;
    return (
      <ProfileMediaRow
        thumbLabel={T.DECK_THUMB}
        title={T.PITCH_DECK}
        subtitle={L.NOT_ADDED_YET}
        action={add}
        missing
      />
    );
  }
  // A deck IS attached but its link could not be signed: say so, never "not added".
  const view = data.pitchDeckDownloadUrl ? (
    <a href={data.pitchDeckDownloadUrl} target="_blank" rel="noreferrer" className={LINK_CLASS}>
      {L.VIEW}
    </a>
  ) : undefined;
  const size =
    data.pitchDeckFileSizeBytes != null ? formatBytes(data.pitchDeckFileSizeBytes) : undefined;
  const subtitle = data.pitchDeckDownloadUrl ? size : T.LINK_UNAVAILABLE;
  return (
    <ProfileMediaRow
      thumbLabel={T.DECK_THUMB}
      title={fileNameOf(data.pitchDeckUrl)}
      subtitle={subtitle}
      action={view}
    />
  );
}

function VideoRow({ data, onAdd }: StartupCardProps) {
  if (!data.pitchVideoUrl) {
    const add = onAdd ? (
      <Button variant="link-sm" onClick={onAdd}>
        {L.ADD_LINK}
      </Button>
    ) : undefined;
    return (
      <ProfileMediaRow title={T.PITCH_VIDEO} subtitle={L.NOT_ADDED_YET} action={add} missing />
    );
  }
  const watch = (
    <a href={data.pitchVideoUrl} target="_blank" rel="noreferrer" className={LINK_CLASS}>
      {L.WATCH}
    </a>
  );
  return <ProfileMediaRow title={T.PITCH_VIDEO} subtitle={data.pitchVideoUrl} action={watch} />;
}

/** Pitch materials (mockup: 5 of 12 columns, deck and video as rows). */
export function PitchMaterialsCard(props: StartupCardProps) {
  // The shared view-only page shows only what exists (FR-180).
  const hideEmpty = useContext(ProfileHideEmptyContext);
  return (
    <ProfileCard title={T.PITCH_MATERIALS} action={props.action} stacked>
      {(props.data.pitchDeckUrl || !hideEmpty) && <DeckRow {...props} />}
      {(props.data.pitchVideoUrl || !hideEmpty) && <VideoRow {...props} />}
    </ProfileCard>
  );
}
